import React from 'react';
import { motion } from 'framer-motion';
import Tilt from 'react-parallax-tilt';
import { Code, Laptop, Smartphone, Brain, Palette, Cloud, ArrowRight } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  web: Code,
  software: Laptop,
  mobile: Smartphone,
  ai: Brain,
  design: Palette,
  cloud: Cloud
};

const Services = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section id="services" className="relative py-32 bg-black overflow-hidden">
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(255,255,255,0.1) 1px, transparent calc(7.6923% + 1px)),
                             repeating-linear-gradient(-90deg, #fff, #fff 1px, transparent 1px, transparent 7.6923%)`
          }}
        />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-[7.6923%]">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block text-[#00FFD1] text-lg font-medium mb-4 tracking-wide"
          >
            OUR SERVICES
          </motion.span>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            What We <span className="text-[#00FFD1]">Deliver</span>
          </h2>
          <p className="text-xl text-white/85 max-w-3xl mx-auto leading-relaxed">
            Comprehensive digital solutions tailored to elevate your business with cutting-edge technology
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <motion.div key={service.id} variants={itemVariants}>
                <Tilt
                  tiltMaxAngleX={5}
                  tiltMaxAngleY={5}
                  glareEnable={true}
                  glareMaxOpacity={0.15}
                  glareColor="#00FFD1"
                  glarePosition="all"
                  glareBorderRadius="0px"
                  scale={1.02}
                  transitionSpeed={400}
                >
                  <div className="group relative bg-[#121212] p-8 h-full border border-white/25 transition-all duration-400 hover:border-[#00FFD1] overflow-hidden">
                    {/* Hover Gradient Effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#00FFD1]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                    
                    <div className="relative z-10">
                      {/* Icon */}
                      <div className="mb-6 inline-flex p-4 bg-[#00FFD1]/10 transition-all duration-400 group-hover:bg-[#00FFD1]/20">
                        <IconComponent className="text-[#00FFD1]" size={36} strokeWidth={1.5} />
                      </div>

                      {/* Title */}
                      <h3 className="text-2xl font-bold text-white mb-4 leading-tight group-hover:text-[#00FFD1] transition-colors duration-400">
                        {service.title}
                      </h3>

                      {/* Description */}
                      <p className="text-white/85 text-base leading-relaxed mb-6">
                        {service.description}
                      </p>

                      {/* Features */}
                      <ul className="space-y-2 mb-6">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-white/70 text-sm">
                            <div className="w-1.5 h-1.5 bg-[#00FFD1] flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      {/* Learn More Link */}
                      <div className="flex items-center gap-2 text-[#00FFD1] font-medium text-sm group-hover:gap-3 transition-all duration-300 cursor-pointer">
                        <span>Learn More</span>
                        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </div>
                    </div>
                  </div>
                </Tilt>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
